import React, { useEffect, useState } from "react";
import { useSOC } from "../context/SOCContext";
import { DataTable } from "../../common/DataTable";
import Drawer, { DrawerSection, Timeline } from "../ui/Drawer";
import { StatusBadge } from "../ui/Badges";
import ApiLoader from "../../ui/ApiLoader";
import { INVESTIGATION_STATUSES } from "../data/mockData";

const Investigations = ({ focusId, onConsumeFocus, onNavigate }) => {
  const { investigations, alerts, incidents, updateInvestigationStatus, loading, errors } = useSOC();
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (focusId) {
      const inv = investigations.find((i) => i.id === focusId);
      if (inv) setSelected(inv);
      onConsumeFocus && onConsumeFocus();
    }
    // eslint-disable-next-line
  }, [focusId, investigations]);

  const uniqueAnalysts = [...new Set(investigations.map((i) => i.analyst).filter(Boolean))];
  const uniquePriorities = [...new Set(investigations.map((i) => i.priority).filter(Boolean))];

  const columns = [
    {
      id: "id",
      accessorFn: (r) => r.id,
      header: "Investigation ID",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Investigation ID", placeholder: "Search investigation ID" } },
      cell: ({ row }) => <span>{row.original.id}</span>,
    },
    {
      id: "title",
      accessorFn: (r) => r.title,
      header: "Title",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Title", placeholder: "Search title" } },
      cell: ({ row }) => <span>{row.original.title}</span>,
    },
    {
      id: "relatedAlertId",
      accessorFn: (r) => r.relatedAlertId,
      header: "Related Alert",
      enableSorting: false,
      cell: ({ row }) => <span>{row.original.relatedAlertId}</span>,
    },
    {
      id: "relatedIncidentId",
      accessorFn: (r) => r.relatedIncidentId,
      header: "Related Incident",
      enableSorting: false,
      cell: ({ row }) => <span>{row.original.relatedIncidentId}</span>,
    },
    {
      id: "analyst",
      accessorFn: (r) => r.analyst,
      header: "Analyst",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Analyst",
          placeholder: "All analysts",
          options: uniqueAnalysts.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.analyst}</span>,
    },
    {
      id: "priority",
      accessorFn: (r) => r.priority,
      header: "Priority",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Priority",
          placeholder: "All priorities",
          options: uniquePriorities.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.priority}</span>,
    },
    {
      id: "status",
      accessorFn: (r) => r.status,
      header: "Status",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Status",
          placeholder: "All statuses",
          options: INVESTIGATION_STATUSES.map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      id: "startedAt",
      accessorFn: (r) => r.startedAt,
      header: "Started At",
      enableSorting: false,
      cell: ({ row }) => <span>{new Date(row.original.startedAt).toLocaleString()}</span>,
    },
    {
      id: "lastUpdated",
      accessorFn: (r) => r.lastUpdated,
      header: "Last Updated",
      enableSorting: false,
      cell: ({ row }) => <span>{new Date(row.original.lastUpdated).toLocaleString()}</span>,
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }) => (
        <button
          onClick={() => setSelected(row.original)}
          className="text-blue-600 hover:underline text-xs font-medium"
        >
          View
        </button>
      ),
    },
  ];

  const alert = selected ? alerts.find((a) => a.id === selected.relatedAlertId) : null;
  const incident = selected ? incidents.find((i) => i.id === selected.relatedIncidentId) : null;

  if (loading.investigations && investigations.length === 0) {
    return <ApiLoader variant="spinner" label="Loading Investigations..." className="py-20" />;
  }

  return (
    <div>
      <DataTable
        columns={columns}
        data={investigations}
        getRowId={(r) => String(r.id)}
        emptyMessage={errors.investigations || "No records found"}
        enableColumnVisibility={false}
        enableColumnResizing
        enableColumnFilters
        maxBodyHeight="65vh"
      />

      <Drawer open={!!selected} onClose={() => setSelected(null)} title={selected?.title} subtitle={selected?.id}>
        {selected && (
          <>
            <DrawerSection label="Summary">
              <p>{selected.summary}</p>
            </DrawerSection>

            <DrawerSection label="Linked Alert / Incident">
              <div className="flex gap-2">
                {alert && (
                  <button onClick={() => onNavigate("alerts", alert.id)} className="text-blue-600 hover:underline">
                    {alert.name}
                  </button>
                )}
                {incident && (
                  <button onClick={() => onNavigate("incidents", incident.id)} className="text-blue-600 hover:underline">
                    {incident.name}
                  </button>
                )}
              </div>
            </DrawerSection>

            <DrawerSection label="Evidence">
              <ul className="list-disc pl-4 space-y-1">
                {selected.evidence.map((ev, i) => (
                  <li key={i}>{ev}</li>
                ))}
              </ul>
            </DrawerSection>

            <DrawerSection label="Assets / Users / IPs">
              <p className="text-gray-500">Assets: {selected.assets.join(", ") || "-"}</p>
              <p className="text-gray-500">Users: {selected.users.join(", ") || "-"}</p>
              <p className="text-gray-500">IPs: {selected.ips.filter(Boolean).join(", ") || "-"}</p>
            </DrawerSection>

            <DrawerSection label="IOCs">
              {selected.iocs.length === 0 ? <p className="text-gray-400">No IOCs recorded.</p> : selected.iocs.map((i, idx) => <p key={idx}>{i}</p>)}
            </DrawerSection>

            <DrawerSection label="Update Status">
              <div className="flex flex-wrap gap-2">
                {INVESTIGATION_STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => updateInvestigationStatus(selected.id, s)}
                    className={`px-3 py-1.5 text-xs rounded-lg border ${
                      selected.status === s
                        ? "bg-blue-100 border-blue-300 text-blue-700"
                        : "border-gray-200 text-gray-500 hover:bg-gray-100"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </DrawerSection>

            <DrawerSection label="Investigation Timeline">
              <Timeline items={selected.timeline} />
            </DrawerSection>

            <DrawerSection label="Findings">
              <p>{selected.findings || "No findings recorded yet."}</p>
            </DrawerSection>

            <DrawerSection label="Recommended Actions">
              {selected.recommendedActions.length === 0 ? (
                <p className="text-gray-400">None recorded.</p>
              ) : (
                selected.recommendedActions.map((a, i) => <p key={i}>{a}</p>)
              )}
            </DrawerSection>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default Investigations;
